/** @format */
import React, { FunctionComponent, useCallback, useEffect, useRef, useState } from 'react';
import {
    SimCardServiceTypeAllowedParam,
    SimCardServiceTypeAllowedParamMap,
    SimCardServiceTypeParam,
    SimCardServiceTypeSingleParam,
} from '../../../../interfaces/collections/SimCardServiceInterface';
import GridContainer from '../../../../components/Grid/GridContainer';
import { DefaultInput } from './SimCardServiceTypeForm/DefaultInput';
import { SelectInput } from './SimCardServiceTypeForm/SelectInput';
import { NumericInput } from './SimCardServiceTypeForm/NumericInput';
import { MultiInput } from './SimCardServiceTypeForm/MultiInput';

const _ = require('underscore');

type OplSimCardServiceTypeParamItemBmeFormType = {
    simCardServiceTypeAllowedParam: SimCardServiceTypeAllowedParam;
    simCardServiceTypeParam?: SimCardServiceTypeParam;
    getFormValues: (formValues: any) => void;
};

export const preparePartnerParams = (
    paramName: string,
    allowedParams: SimCardServiceTypeAllowedParamMap,
    definedParam?: SimCardServiceTypeAllowedParamMap | null
): SimCardServiceTypeSingleParam => {
    const paramObj = {} as SimCardServiceTypeSingleParam;
    paramObj.paramName = paramName;
    paramObj.globalParams = allowedParams;
    if (definedParam) {
        paramObj.params = _.clone(definedParam);
    } else {
        paramObj.params = _.clone(allowedParams);
    }
    return paramObj;
};

export const OplSimCardServiceTypeParamItemBmeForm: FunctionComponent<OplSimCardServiceTypeParamItemBmeFormType> = ({
    simCardServiceTypeAllowedParam,
    simCardServiceTypeParam,
    getFormValues,
}): React.ReactElement => {
    const [formValues, setFormValues] = useState<any>(null);
    const createDefaultFormValues = useCallback(
        (paramName: string, objectValue: object): object => {
            let x = { ...objectValue };
            // eslint-disable-next-line @typescript-eslint/ban-ts-ignore
            // @ts-ignore
            if (x.allowedValue && Array.isArray(objectValue.allowedValue)) {
                // eslint-disable-next-line @typescript-eslint/ban-ts-ignore
                // @ts-ignore
                x.allowedValue = objectValue.allowedValue.map((el: any) => el);
            } else {
                // eslint-disable-next-line @typescript-eslint/ban-ts-ignore
                // @ts-ignore
                x.allowedValue = [];
            }
            // eslint-disable-next-line @typescript-eslint/ban-ts-ignore
            // @ts-ignore
            x.input = getInputValue(objectValue);
            // eslint-disable-next-line @typescript-eslint/ban-ts-ignore
            // @ts-ignore
            x.type = objectValue.input ?? objectValue.parameterType ? objectValue.parameterType.toLowerCase() : 'string';
            // eslint-disable-next-line @typescript-eslint/ban-ts-ignore
            // @ts-ignore
            x['param_name'] = paramName;
            // eslint-disable-next-line @typescript-eslint/ban-ts-ignore
            // @ts-ignore
            x.default = objectValue.default ?? null;

            // type, default, input, allowedValue, required, param_name, allowedType
            // eslint-disable-next-line @typescript-eslint/ban-ts-ignore
            // @ts-ignore
            const { parentName, parameterValue, parameterType, guiView, ...z } = x;

            return { ...formValues, [paramName]: z };
        },
        [formValues]
    );

    const getValues = (paramName: string, values: any): void => {
        setFormValues({ ...formValues, [paramName]: { ...formValues[paramName], ...values } });
    };

    const getInputValue = (value: any): string => {
        if (value.allowedType && value.allowedType.includes('array')) {
            return 'select';
        } else {
            return 'numeric';
        }
    };

    const getProperComponent = (
        parameter: any,
        parameterKey: string,
        createValuesFromForm: (paramName: string, objectValue: object) => void | null,
        assignedValue?: any
    ): React.ReactElement => {
        let component: any = null;
        if (parameter.guiView) {
            let x = { ...parameter };
            if (x.allowedValue && Array.isArray(parameter.allowedValue)) {
                // eslint-disable-next-line @typescript-eslint/ban-ts-ignore
                // @ts-ignore
                x.allowedValue = parameter.allowedValue.map((el: any) => {
                    return {
                        value: el,
                    };
                });
            }

            if (parameter.allowedValue === 'string') {
                component = (
                    <DefaultInput
                        key={parameterKey}
                        isBME={true}
                        parameter={x}
                        parameterKey={parameterKey}
                        createValuesFromForm={createValuesFromForm}
                        assignedValue={assignedValue}
                    />
                );
            }

            if (Array.isArray(parameter.allowedValue)) {
                component = (
                    <SelectInput
                        key={parameterKey}
                        isBME={true}
                        parameter={x}
                        parameterKey={parameterKey}
                        createValuesFromForm={createValuesFromForm}
                        assignedValue={assignedValue}
                    />
                );
            }

            if (parameter.allowedValue === '/d+') {
                component = (
                    <NumericInput
                        key={parameterKey}
                        isBME={true}
                        parameter={x}
                        parameterKey={parameterKey}
                        createValuesFromForm={createValuesFromForm}
                        assignedValue={assignedValue}
                    />
                );
            }

            if (parameter.allowedValue === 'array') {
                component = (
                    <MultiInput
                        key={parameterKey}
                        isBME={true}
                        parameter={x}
                        parameterKey={parameterKey}
                        createValuesFromForm={createValuesFromForm}
                        assignedValue={assignedValue}
                    />
                );
            }
        }

        return component;
    };

    const getForm = (): Array<object> => {
        const arr = [];

        let assignedValues = null;
        if (simCardServiceTypeParam) {
            assignedValues = JSON.parse(simCardServiceTypeParam.parameters);
        }
        for (const [key, value] of Object.entries(JSON.parse(simCardServiceTypeAllowedParam.parameters)['IN_ZTE'])) {
            if (key === 'parameters') {
                // eslint-disable-next-line @typescript-eslint/ban-ts-ignore
                // @ts-ignore
                for (const [key2, value2] of Object.entries(value)) {
                    // IF below temporary quick fix
                    if (['amount', 'balMode', 'walletExpiryDateOffset', 'balanceExpiryDateOffset', 'BMPClientName'].includes(key2)) {
                        // eslint-disable-next-line @typescript-eslint/ban-ts-ignore
                        // @ts-ignore
                        const x = getProperComponent(value2, key2, getValues, assignedValues ? assignedValues[key2] : null);
                        arr.push(x);
                    }
                }
            }
        }

        return arr;
    };

    useEffect(() => {
        if (!formValues) {
            let arr = {};
            if (!simCardServiceTypeParam) {
                for (const [key, value] of Object.entries(JSON.parse(simCardServiceTypeAllowedParam.parameters)['IN_ZTE'])) {
                    if (key === 'parameters') {
                        // eslint-disable-next-line @typescript-eslint/ban-ts-ignore
                        // @ts-ignore
                        for (const [key2, value2] of Object.entries(value)) {
                            if (['amount', 'balMode', 'walletExpiryDateOffset', 'balanceExpiryDateOffset', 'BMPClientName'].includes(key2)) {
                                // eslint-disable-next-line @typescript-eslint/ban-ts-ignore
                                // @ts-ignore
                                const x = createDefaultFormValues(key2, value2);
                                arr = { ...arr, ...x };
                            }
                        }
                    }
                }
            } else {
                const assignedValues = JSON.parse(simCardServiceTypeParam.parameters);
                for (const [key, value] of Object.entries(JSON.parse(simCardServiceTypeAllowedParam.parameters)['IN_ZTE'])) {
                    if (key === 'parameters') {
                        // eslint-disable-next-line @typescript-eslint/ban-ts-ignore
                        // @ts-ignore
                        for (const [key2, value2] of Object.entries(value)) {
                            if (['amount', 'balMode', 'walletExpiryDateOffset', 'balanceExpiryDateOffset', 'BMPClientName'].includes(key2)) {
                                let z = {
                                    // eslint-disable-next-line @typescript-eslint/ban-ts-ignore
                                    // @ts-ignore
                                    ...value2,
                                    ...assignedValues[key2],
                                };
                                // eslint-disable-next-line @typescript-eslint/ban-ts-ignore
                                // @ts-ignore
                                const x = createDefaultFormValues(key2, z);
                                arr = { ...arr, ...x };
                            }
                        }
                    }
                }
            }

            setFormValues(arr);
        }
    }, [formValues, createDefaultFormValues, simCardServiceTypeAllowedParam, simCardServiceTypeParam]);

    useEffect(() => {
        getFormValues(formValues);
    }, [formValues, getFormValues]);

    return <GridContainer>{getForm()}</GridContainer>;
};
